import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'centaiva-sidebar-open';

/**
 * Single source of truth for sidebar open/collapsed state.
 * Persists to localStorage so page refresh keeps the user's preference.
 * Handles responsive defaults: tablet/mobile start collapsed/closed.
 */
@Injectable({ providedIn: 'root' })
export class SidebarStateService {
  readonly width = signal<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);
  private readonly _open = signal<boolean>(this.readStorage());

  /** True = sidebar fully visible (expanded), false = compact rail or mobile drawer closed. */
  readonly open = this._open.asReadonly();

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        this.width.set(window.innerWidth);
      });
    }
  }

  isMobile(): boolean {
    return this.width() < 640;
  }

  isTablet(): boolean {
    return this.width() >= 640 && this.width() < 900;
  }

  isTabletOrDesktop(): boolean {
    return this.width() >= 640;
  }

  toggle(): void {
    const next = !this._open();
    this._open.set(next);
    try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* private/SSR */ }
  }

  close(): void {
    this._open.set(false);
  }

  private readStorage(): boolean {
    try {
      if (typeof window !== 'undefined' && window.innerWidth < 900) {
        return false; // default to collapsed/closed on smaller viewports
      }
      const v = localStorage.getItem(STORAGE_KEY);
      return v === null ? true : v === 'true'; // default: open
    } catch { return true; }
  }
}
